import { useMemo, useState } from 'react';
import { SESSIONS } from '../data/sessions';
import useReveal from './useReveal';
import Lightbox from './Lightbox';
import Icon from './Icon';
import './Sessions.css';

const ALL = 'all';

// Fixed order for the filter tabs; only categories actually present are shown.
const CATEGORY_ORDER = [
  { key: 'posh-awareness', label: 'POSH Awareness' },
  { key: 'ic-training', label: 'IC Training' },
  { key: 'self-improvement', label: 'Self Improvement' },
  { key: 'communication-skills', label: 'Communication Skills' },
  { key: 'future-ready-teens', label: 'Future Ready Teens' },
  { key: 'child-pocso', label: 'Child POSH-POCSO' },
];

export default function Sessions({ showHeader = true }) {
  const [filter, setFilter] = useState(ALL);
  const [active, setActive] = useState(null); // the session open in the lightbox

  const tabs = useMemo(() => {
    const present = new Set(SESSIONS.map((s) => s.category));
    return [
      { key: ALL, label: 'All' },
      ...CATEGORY_ORDER.filter((c) => present.has(c.key)),
    ];
  }, []);

  const visible = useMemo(
    () => (filter === ALL ? SESSIONS : SESSIONS.filter((s) => s.category === filter)),
    [filter]
  );

  // Re-key the grid on filter change so useReveal re-runs its stagger animation.
  const gridRef = useReveal({ stagger: 70, maxStaggered: 6 });

  // Nothing to show until photos are processed — keep the page clean pre-upload.
  if (SESSIONS.length === 0) return null;

  return (
    <section id="sessions" className="sessions">
      <div className="container">
        {showHeader && (
          <header className="sessions__header">
            <p className="eyebrow">In the field</p>
            <h2 className="sessions__title">Sessions delivered across workplaces &amp; campuses.</h2>
            <p className="sessions__sub">
              A glimpse of sessions conducted, with real teams, in real rooms.
            </p>
          </header>
        )}

        {tabs.length > 2 && (
          <div className="sessions__filters" role="tablist" aria-label="Filter sessions by program">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={filter === t.key}
                className={`sessions__tab ${filter === t.key ? 'is-active' : ''}`}
                onClick={() => setFilter(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        <div className="sessions__grid" ref={gridRef} key={filter}>
          {visible.map((s) => (
            <button
              key={s.slug}
              type="button"
              className="session-card"
              onClick={() => setActive(s)}
              aria-label={`View photos from ${s.title}${s.venue ? `, ${s.venue}` : ''}`}
            >
              <span className="session-card__media">
                <img
                  className="session-card__img"
                  src={s.cover}
                  alt={`${s.title}${s.venue ? ` — ${s.venue}` : ''}`}
                  loading="lazy"
                  decoding="async"
                />
                <span className="session-card__scrim" aria-hidden="true" />
                <span className="session-card__tag">{s.categoryLabel}</span>
                <span className="session-card__count">
                  <Icon name="images" size={14} className="session-card__count-icon" />
                  Photos
                </span>
                <span className="session-card__view" aria-hidden="true">
                  <Icon name="expand" size={22} />
                </span>
              </span>
              <span className="session-card__body">
                <span className="session-card__name">{s.title}</span>
                {(s.venue || s.date) && (
                  <span className="session-card__meta">
                    {s.venue && <Icon name="pin" size={14} className="session-card__meta-icon" />}
                    {[s.venue, s.date].filter(Boolean).join(' · ')}
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>
      </div>

      {active && (
        <Lightbox
          photos={active.photos}
          title={[active.title, active.venue].filter(Boolean).join(' · ')}
          onClose={() => setActive(null)}
        />
      )}
    </section>
  );
}
